import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Save, Plus, Trash2, GripVertical } from "lucide-react";
import { heroService } from "@/services/firebase";
import { CloudinaryImageField } from "@/components/admin/CloudinaryImageField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import toast from "react-hot-toast";
import type { HeroSection, HeroStat } from "@/types";

export function HeroAdminPage() {
  const [hero, setHero] = useState<HeroSection | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    heroService.getAll().then((items) => {
      setHero(items[0] || null);
      setLoading(false);
    });
  }, []);

  const handleSave = async () => {
    if (!hero) return;
    setSaving(true);
    console.log("Sauvegarde du hero avec image:", hero.backgroundImage);
    try {
        await heroService.update(hero.id, {
          title: hero.title,
          subtitle: hero.subtitle,
          description: hero.description,
          backgroundImage: hero.backgroundImage,
          ctaClientText: hero.ctaClientText,
          ctaClientLink: hero.ctaClientLink,
          ctaArtisanText: hero.ctaArtisanText,
        ctaArtisanLink: hero.ctaArtisanLink,
        stats: hero.stats,
      });
      toast.success("Hero section mis à jour");
    } catch {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const updateStat = (id: string, field: keyof HeroStat, value: string) => {
    if (!hero) return;
    setHero({
      ...hero,
      stats: hero.stats.map((s) => (s.id === id ? { ...s, [field]: value } : s)),
    });
  };

  const addStat = () => {
    if (!hero) return;
    const newStat: HeroStat = {
      id: Date.now().toString(),
      value: "0",
      label: "Nouveau",
      icon: "users",
    };
    setHero({ ...hero, stats: [...hero.stats, newStat] });
  };

  const removeStat = (id: string) => {
    if (!hero) return;
    setHero({ ...hero, stats: hero.stats.filter((s) => s.id !== id) });
  };

  if (loading) return <div className="animate-pulse">Chargement...</div>;
  if (!hero) return <div>Aucune donnée hero trouvée</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-dark">Gestion Hero</h1>
          <p className="text-gray-500 text-sm">Modifier le contenu de la bannière principale</p>
        </div>
        <Button onClick={handleSave} isLoading={saving}>
          <Save className="w-4 h-4 mr-2" />
          Sauvegarder
        </Button>
      </div>

      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Contenu principal</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              label="Titre principal"
              value={hero.title}
              onChange={(e) => setHero({ ...hero, title: e.target.value })}
            />
            <Input
              label="Sous-titre"
              value={hero.subtitle}
              onChange={(e) => setHero({ ...hero, subtitle: e.target.value })}
            />
            <Textarea
              label="Description"
              value={hero.description}
              onChange={(e) => setHero({ ...hero, description: e.target.value })}
              rows={3}
            />
            <CloudinaryImageField
              label="Image de fond"
              value={hero.backgroundImage}
              folder="hero"
              onChange={(backgroundImage) => setHero({ ...hero, backgroundImage })}
              hint="Image optionnelle utilisée comme fond du hero côté vitrine."
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Boutons d'action</CardTitle>
          </CardHeader>
          <CardContent className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-4">
              <Input
                label="CTA Client - Texte"
                value={hero.ctaClientText}
                onChange={(e) => setHero({ ...hero, ctaClientText: e.target.value })}
              />
              <Input
                label="CTA Client - Lien"
                value={hero.ctaClientLink}
                onChange={(e) => setHero({ ...hero, ctaClientLink: e.target.value })}
              />
            </div>
            <div className="space-y-4">
              <Input
                label="CTA Artisan - Texte"
                value={hero.ctaArtisanText}
                onChange={(e) => setHero({ ...hero, ctaArtisanText: e.target.value })}
              />
              <Input
                label="CTA Artisan - Lien"
                value={hero.ctaArtisanLink}
                onChange={(e) => setHero({ ...hero, ctaArtisanLink: e.target.value })}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Statistiques</CardTitle>
            <Button size="sm" variant="outline" onClick={addStat}>
              <Plus className="w-4 h-4 mr-1" />
              Ajouter
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {hero.stats.map((stat) => (
              <div key={stat.id} className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                <GripVertical className="w-5 h-5 text-gray-400 mt-2" />
                <div className="flex-1 grid sm:grid-cols-4 gap-3">
                  <Input
                    placeholder="Valeur"
                    value={stat.value}
                    onChange={(e) => updateStat(stat.id, "value", e.target.value)}
                  />
                  <Input
                    placeholder="Label"
                    value={stat.label}
                    onChange={(e) => updateStat(stat.id, "label", e.target.value)}
                  />
                  <Input
                    placeholder="Suffixe"
                    value={stat.suffix || ""}
                    onChange={(e) => updateStat(stat.id, "suffix", e.target.value)}
                  />
                  <Input
                    placeholder="Icône"
                    value={stat.icon}
                    onChange={(e) => updateStat(stat.id, "icon", e.target.value)}
                  />
                </div>
                <button
                  onClick={() => removeStat(stat.id)}
                  className="p-2 text-red-400 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
